import { BreadcrumbItem } from '@/types/services';

export const CONTENTSET_TITLE = '課題解決編';
export const BASE_URL = '/spot-live/problem-solving';

export type problemSolvingOverViewItem = {
    chapter_title: string;
    chapter_name: string;
    description: string;
    chapter_detail_1: string;
    chapter_detail_2?: string;
    chapter_detail_3?: string;
};

export type usageTipsItem = {
    title: string;
    tips: string[];
};
export type useCaseItem = {
    title: string;
    description: string;
    use_case_detail: string;
    imageWidth: number;
};
export type codeExampleItem = {
    images: imageItem[];
};

export type imageItem = {
    title: string;
    baseWidth: number;
    lgWidth: number;
};

export type problemSolvingDataItem = {
    contentsCd: string;
    chapterType: ChapterType;
    problemSolvingOverView: problemSolvingOverViewItem;
};

export type ChapterType =
    | 'chapter01'
    | 'chapter02'
    | 'chapter03'
    | 'chapter04'
    | 'chapter05'
    | 'chapter06'
    | 'chapter07'
    | 'chapter08';

export const contentCdMap: Record<ChapterType, string> = {
    chapter01: '01',
    chapter02: '02',
    chapter03: '03',
    chapter04: '04',
    chapter05: '05',
    chapter06: '06',
    chapter07: '07',
    chapter08: '08',
};

export const PROBLEM_SOLVING_DATA: readonly problemSolvingDataItem[] = [
    {
        contentsCd: '01',
        chapterType: 'chapter01',
        problemSolvingOverView: {
            chapter_title: 'センサーやデバイスに触れよう',
            chapter_name: 'センシング& アクション',
            description:
                'センサーコネクターの使い方を知り、データを取得する方法を考えます。\n例えば、距離センサーは水面に反射したり、明るさセンサーでは色を感知することができたり、磁気センサーでドアの開閉などを感知することができます。それぞれのセンサーの特徴を知り、様々な活用方法を学びます。',
            chapter_detail_1: 'センサーの利用',
            chapter_detail_2: 'センサー活用事例',
        },
    },
    {
        contentsCd: '02',
        chapterType: 'chapter02',
        problemSolvingOverView: {
            chapter_title: 'プログラミングで解決しよう',
            chapter_name: 'ゲーミフィケーション',
            description:
                '課題を解決する方法について、社会の事例とともに学びます。課題の解決には、アラームがなって注意する解決方法もあれば、ゲーム性を持って行動を誘導することで解決する方法もあります。例えば、ゴミ箱にゴミを入れたらルーレットが始まりゲームができるというものもあれば、スクワットをすることで電車賃が安くなるような仕組みを採用している国もあります。このような社会の事例を学びつつ、そこで活用されているプログラミング技術を学びます。',
            chapter_detail_1: 'ゲーミフィケーションを意識した課題解決',
            chapter_detail_2: '行動心理学を意識した課題解決',
        },
    },
    {
        contentsCd: '03',
        chapterType: 'chapter03',
        problemSolvingOverView: {
            chapter_title: '企画を立てよう',
            chapter_name: 'イノベーション',
            description:
                '学校の探究学習の中で取り組むテーマについて考えます。これまでの知識と技術を活用して学校の中、身の回りの生活、地域の課題解決や地域貢献について考えていきます。課題を発見し、問いを設定する方法を学びます。',
            chapter_detail_1: '身近な課題を解決するための企画設計',
            chapter_detail_2: '地域の課題解決を行うための問いの設定',
        },
    },
    {
        contentsCd: '04',
        chapterType: 'chapter04',
        problemSolvingOverView: {
            chapter_title: '設計を進めよう',
            chapter_name: 'イノベーション',
            description:
                '課題を解決するためのプロトタイプの開発を行います。ユーザーに使ってもらうための簡単なプロトタイプをつくる方法を学びます。',
            chapter_detail_1: 'プロトタイプ開発',
            chapter_detail_2: '形にして動かすための準備',
        },
    },
    {
        contentsCd: '05',
        chapterType: 'chapter05',
        problemSolvingOverView: {
            chapter_title: '開発を進めよう',
            chapter_name: 'イノベーション',
            description:
                'これまで習得した知識や技術を基に、プロトタイプを完成させます。ユーザーの声や開発の目的に立ち返り、目的やニーズに応えることのできる仕組みに辿り着くための考え方を学びます。',
            chapter_detail_1: 'プロトタイプの完成',
            chapter_detail_2: '開発テーマの再確認',
        },
    },
    {
        contentsCd: '06',
        chapterType: 'chapter06',
        problemSolvingOverView: {
            chapter_title: '完成させよう',
            chapter_name: 'イノベーション',
            description:
                'これから開発を進めていくために必要な役割分担やスケジュール管理について学びます。実際に高校生が開発したeDIYギアの事例を紹介しながら、どのように開発を進めていくべきかを考えます。',
            chapter_detail_1: '役割分担などのプロジェクトマネジメント',
            chapter_detail_2: '過去の開発事例の紹介',
        },
    },
    {
        contentsCd: '07',
        chapterType: 'chapter07',
        problemSolvingOverView: {
            chapter_title: 'フィードバックをもらい改善しよう',
            chapter_name: 'イノベーション',
            description:
                'つくったプロトタイプをユーザーに使ってもらい、フィードバックをもらいます。フィードバックをもとにプロトタイプを改善していく方法を学びます。',
            chapter_detail_1: 'プロトタイプを使ってもらうための準備',
            chapter_detail_2: 'フィードバックをもらい開発に活かす',
        },
    },
    {
        contentsCd: '08',
        chapterType: 'chapter08',
        problemSolvingOverView: {
            chapter_title: '展開しよう',
            chapter_name: 'イノベーション',
            description:
                '開発したプロトタイプについての発表を行います。他の学校や地域でも展開していけるように魅力的なプレゼンができるよう学びます。',
            chapter_detail_1: '展開するためのプレゼンテーション',
            chapter_detail_2: '他の人に見習ってもらい使ってもらうための表現方法',
        },
    },
];

export const BREADCRUMB_PROBLEM_SOLVING_ITEMS: Record<string, BreadcrumbItem[]> = {
    [BASE_URL]: [{ label: `${CONTENTSET_TITLE} 目次` }],
    ...Object.fromEntries(
        PROBLEM_SOLVING_DATA.map((item) => [
            `${BASE_URL}/${item.chapterType}`,
            [{ label: CONTENTSET_TITLE, href: BASE_URL }, { label: `CHAPTER${item.contentsCd}` }],
        ]),
    ),
};
